import { Song, SongUsage } from "@/javascript/library-objects";
import {
    Button,
    Flex,
    ScrollArea,
    Skeleton,
    Stack,
    Table,
    Text,
} from "@mantine/core";
import { IconEye, IconEyeClosed } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import ConductionModal from "../ConductionModal";

interface SongUsageStatsBrowserProps {
    onClose: () => void;
    songs: Song[];
}

const SongUsageStatsBrowser: React.FC<SongUsageStatsBrowserProps> = ({ songs, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [artistSongs, setArtistSongs] = useState<{ [key: string]: Song[] }>({});
    const [expandedArtists, setExpandedArtists] = useState<string[]>([]);

    // Extract unique composer artists from all songs
    const artists = songs
        .map((song) => song.artists)
        .flat()
        .filter((artist) => artist.artistType.toLowerCase() === "composer")
        .filter(
            (artist, index, self) =>
                self.findIndex((a) => a.artistId === artist.artistId) === index
        );

    useEffect(() => {
        // Build a dictionary mapping artistId to their songs
        const loadedArtistSongs: { [key: string]: Song[] } = {};
        artists.forEach((artist) => {
            loadedArtistSongs[artist.artistId] = songs.filter((song) =>
                song.artists.some((a) => a.artistId === artist.artistId)
            );
        });
        setArtistSongs(loadedArtistSongs);
        setLoading(false);
    }, [songs, artists]);

    const toggleExpand = (artistId: string) => {
        setExpandedArtists((prev) =>
            prev.includes(artistId)
                ? prev.filter((id) => id !== artistId)
                : [...prev, artistId]
        );
    };

    return (
        <ConductionModal
            title="Song Usage Statistics"
            size="80%"
            onClose={onClose}
        >
            <Stack align="center" justify="center" style={{ width: "100%" }}>
                <Flex
                    direction="column"
                    align="center"
                    justify="space-between"
                    style={{ width: "100%" }}
                >
                    {loading ? (
                        <Skeleton height="100%" radius="md" />
                    ) : artists.length === 0 ? (
                        <Text>No artists found to display stats for.</Text>
                    ) : (
                        <ScrollArea style={{ width: "100%" }}>
                            <Table
                                highlightOnHover
                                stickyHeader={true}
                            >
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Artist</Table.Th>
                                        <Table.Th>Total Usage in Projects</Table.Th>
                                        <Table.Th>Total Usage in Submissions</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {artists.map((artist) => {
                                        const songsForArtist = artistSongs[artist.artistId] || [];
                                        const artistTotalUsageAll = songsForArtist.reduce(
                                            (acc: number, song: Song) =>
                                                acc +
                                                song.usage.reduce(
                                                    (acc: number, usage: SongUsage) => acc + usage.measuresUsed,
                                                    0
                                                ),
                                            0
                                        );
                                        const artistTotalUsageSubmissions = songsForArtist.reduce(
                                            (acc: number, song: Song) =>
                                                acc +
                                                song.usage.reduce(
                                                    (acc: number, usage: SongUsage) =>
                                                        acc + (usage.submissionCount > 0 ? usage.measuresUsed : 0),
                                                    0
                                                ),
                                            0
                                        );

                                        const isExpanded = expandedArtists.includes(artist.artistId);

                                        return (
                                            <React.Fragment key={artist.artistId}>
                                                <Table.Tr
                                                    onClick={() => toggleExpand(artist.artistId)}
                                                    bg={isExpanded ? "blue.1" : ""}
                                                    style={{
                                                        cursor: "pointer"
                                                    }}
                                                >
                                                    <Table.Td>{artist.artistName}</Table.Td>
                                                    <Table.Td>{artistTotalUsageAll}</Table.Td>
                                                    <Table.Td>{artistTotalUsageSubmissions}</Table.Td>
                                                    <Table.Td>
                                                        <Button
                                                            variant="default"
                                                            size="compact-sm"
                                                        >
                                                            {isExpanded ? (
                                                                <IconEyeClosed size={18} />
                                                            ) : (
                                                                <IconEye size={18} />
                                                            )}
                                                        </Button>
                                                    </Table.Td>
                                                </Table.Tr>
                                                {isExpanded && (
                                                    <Table.Tr
                                                        bg="gray.1"
                                                    >
                                                        <Table.Td
                                                            colSpan={3}
                                                            style={{ paddingLeft: "10%" }}
                                                        >
                                                            <Table>
                                                                <Table.Thead>
                                                                    <Table.Tr>
                                                                        <Table.Th>Song</Table.Th>
                                                                        <Table.Th>Usage in Projects</Table.Th>
                                                                        <Table.Th>Usage in Submissions</Table.Th>
                                                                    </Table.Tr>
                                                                </Table.Thead>
                                                                <Table.Tbody>
                                                                    {songsForArtist.map((song) => {
                                                                        const songUsageAll = song.usage.reduce(
                                                                            (acc: number, usage: SongUsage) =>
                                                                                acc + usage.measuresUsed,
                                                                            0
                                                                        );
                                                                        const songUsageSubmissions = song.usage.reduce(
                                                                            (acc: number, usage: SongUsage) =>
                                                                                acc + (usage.submissionCount > 0 ? usage.measuresUsed : 0),
                                                                            0
                                                                        );
                                                                        return (
                                                                            <Table.Tr key={song.songId}>
                                                                                <Table.Td>{song.songName}</Table.Td>
                                                                                <Table.Td>{songUsageAll}</Table.Td>
                                                                                <Table.Td>{songUsageSubmissions}</Table.Td>
                                                                            </Table.Tr>
                                                                        );
                                                                    })}
                                                                </Table.Tbody>
                                                            </Table>
                                                        </Table.Td>
                                                    </Table.Tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}
                </Flex>

                <Table
                    striped
                    stickyHeader
                    highlightOnHover
                >
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Universal Total for All Projects</Table.Th>
                            <Table.Th>Universal Total for All Submissions</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        <Table.Tr>
                            <Table.Td>
                                {songs.reduce(
                                    (acc: number, song: Song) =>
                                        acc +
                                        song.usage.reduce(
                                            (acc: number, usage: SongUsage) => acc + usage.measuresUsed,
                                            0
                                        ),
                                    0
                                )}
                            </Table.Td>
                            <Table.Td>
                                {songs.reduce(
                                    (acc: number, song: Song) =>
                                        acc +
                                        song.usage.reduce(
                                            (acc: number, usage: SongUsage) =>
                                                acc + (usage.submissionCount > 0 ? usage.measuresUsed : 0),
                                            0
                                        ),
                                    0
                                )}
                            </Table.Td>
                        </Table.Tr>
                    </Table.Tbody>
                </Table>
                <Text
                    size="xs"
                >
                    * All stat units are measures
                </Text>
            </Stack>
        </ConductionModal>
    );
};

export default SongUsageStatsBrowser;
